import { useCallback, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AddFactDialog } from './dialogs/add-fact-dialog';
import { ArchiveSelectedDialog, ArchiveSelectedMenuItem } from './dialogs/archive-selected';
import { FilterFactsDialog } from './dialogs/filter-facts-dialog';
import { UpdateRelevanceButton } from './dialogs/update-relevance';
import { Permission } from '../../../../../../../../../../backend/src/auth/permissions/permission';
import { FactDownloadColumnOptions } from '../../../../../../../../../../backend/src/matter/fact/fact-download';
import { DownloadFormat, StyleFormat } from '../../../../../../../../../../backend/src/types/types';
import { ExportType } from '../../../../../../../../../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import useDownloadFacts from '@/api/mutations/useDownloadFacts';
import { useResolveFactDocIds } from '@/api/mutations/useResolveFactDocIds';
import { useGetCaseDocTypes } from '@/api/queries/useGetCaseDocTypes';
import { useGetCaseIssues } from '@/api/queries/useGetCaseThemes';
import { MatterRun } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import { PermissionGate } from '@/hooks/useHasPermission';
import { DownloadFlow } from '@/new/components/download/DownloadFlow';
import type { AsyncVariant, DownloadDefinition, DownloadVariant } from '@/new/components/download/types';
import { useDownloadController } from '@/new/components/download/useDownloadController';
import { FactChronologyDropdown } from '@/new/components/fact-chronology-dropdown';
import { Icon } from '@/new/components/icon';
import { ClusterFactsModal } from '@/new/components/labeller/cluster-facts';
import { CreateInconsistencyModal } from '@/new/components/labeller/create-inconsistency';
import { NaturalLanguageSearchBox } from '@/new/components/natural-language-search-bar';
import { OverflowMenu } from '@/new/components/overflow-menu';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { ButtonGroup } from '@/new/components/shadcn/button-group';
import { DropdownMenuItem } from '@/new/components/shadcn/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { SubsetDropdown } from '@/new/components/subset-dropdown';
import { useMatterProcessingStore } from '@/new/stores/matter-processing-store';
import { FactFilters as FactFiltersType } from '@/types';

interface FactsEditorToolbarProps {
  globalFilter: string;
  refetchCaseFacts: () => void;
  clearSearch: () => void;
  setGlobalFilter: (value: string) => void;
  onSearchCall: (searchValue?: string, useNaturalLanguage?: boolean) => void;
  goToPage: (value: number) => void;
  activeFilters: FactFiltersType;
  setFilters: (v: FactFiltersType) => void;
  caseRuns: MatterRun[];
  onClusterComplete: () => void;
  processingNL: boolean;
  filterCount: number;
  selectedFactIds: Set<string>;
  setSelectedFactIds: (ids: Set<string>) => void;
  totalCount?: number;
  totalUnfilteredCount?: number;
}

export const FactsTableHeader = ({
  globalFilter,
  selectedFactIds,
  refetchCaseFacts,
  clearSearch,
  setGlobalFilter,
  onSearchCall,
  goToPage,
  activeFilters,
  setFilters,
  caseRuns,
  onClusterComplete,
  processingNL,
  filterCount,
  setSelectedFactIds,
  totalCount,
  totalUnfilteredCount,
}: FactsEditorToolbarProps) => {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) {
    return;
  }

  const { data: issuesWithCounts = [] } = useGetCaseIssues({ matterId, subsetId });
  const { data: docTypes = [] } = useGetCaseDocTypes({ matterId, subsetId });

  // State
  const [factAdderModalIsOpen, setFactAdderModalIsOpen] = useState(false);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [clusterModalOpen, setClusterModalOpen] = useState(false);
  const [createInconsistencyModalOpen, setCreateInconsistencyModalOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);

  const { user } = useUserContext();
  const isLabellingOrg = user?.organisation?.isLabellingOrg ?? false;

  const navigate = useNavigate();
  const resolveFactDocIds = useResolveFactDocIds();
  const isResolvingDocs = resolveFactDocIds.isLoading;

  const download = useDownloadController();
  const { downloadMutation } = useDownloadFacts({ matterId, subsetId });
  const { mutateAsync: fetchDownload } = downloadMutation;

  const downloadDefinition = useMemo<DownloadDefinition>(() => {
    const progressEndpoint = () => `${APIBaseChronos}/api/matter/${matterId}/fact/${subsetId}/export-progress`;
    const statusEndpoint = () => `${APIBaseChronos}/api/matter/${matterId}/fact/${subsetId}/export-status`;
    const selectedScopeId = 'selected';
    const filteredScopeId = 'filtered';
    const allScopeId = 'all';

    const emptyFactFilters: FactFiltersType = {
      docIds: [],
      excludedDocIds: [],
      relevance: [],
      classification: [],
      keyFacts: false,
      disputedFacts: false,
      startDate: null,
      endDate: null,
      runIds: [],
      personIds: [],
      documentTypes: [],
      issueIds: [],
      searchQuery: '',
      factIds: [],
    };

    const resolveFiltersForScope = (scope: string | undefined): FactFiltersType =>
      scope === selectedScopeId
        ? { ...activeFilters, factIds: [...selectedFactIds] }
        : scope === allScopeId
          ? emptyFactFilters
          : activeFilters;

    const tableVariant: AsyncVariant<DownloadFormat> = {
      id: 'facts-table',
      kind: 'table',
      label: 'Table',
      description: 'Word or Excel',
      mode: 'async',
      formats: [DownloadFormat.EXCEL, DownloadFormat.DOCX],
      defaultFilename: 'Facts',
      columns: { available: FactDownloadColumnOptions, default: FactDownloadColumnOptions },
      sourceDocs: true,
      largeExportWarning: true,
      mutate: async (values) => {
        if (values.format === DownloadFormat.EXCEL) {
          trackCustomEvent(TrackingEventsV2.FactDownloadCsv, { matterId, subsetId });
        } else if (values.format === DownloadFormat.DOCX) {
          trackCustomEvent(TrackingEventsV2.FactDownloadDocx, { matterId, subsetId });
        }
        if (values.includeSourceDocuments) {
          trackCustomEvent(TrackingEventsV2.FactDownloadWithDocuments, { matterId, subsetId });
        }
        return fetchDownload({
          title: values.filename,
          columns: values.columns ?? FactDownloadColumnOptions,
          styleFormat: StyleFormat.Table,
          downloadFormat: values.format,
          filters: resolveFiltersForScope(values.scope),
          includeSourceDocuments: values.includeSourceDocuments,
          orderByDate: values.orderByDate,
        });
      },
      progressEndpoint,
      statusEndpoint,
    };

    const writtenVariant: AsyncVariant<DownloadFormat> = {
      id: 'facts-written',
      kind: 'written',
      label: 'Written',
      description: 'Word',
      mode: 'async',
      formats: [DownloadFormat.DOCX],
      defaultFilename: 'Facts',
      style: true,
      styleItemNoun: 'fact',
      docOptions: true,
      sourceDocs: true,
      largeExportWarning: true,
      mutate: async (values) => {
        trackCustomEvent(TrackingEventsV2.FactDownloadDocx, { matterId, subsetId });
        if (values.includeSourceDocuments) {
          trackCustomEvent(TrackingEventsV2.FactDownloadWithDocuments, { matterId, subsetId });
        }
        return fetchDownload({
          title: values.filename,
          style: values.style,
          styleFormat: StyleFormat.Written,
          downloadFormat: values.format,
          documentOptions: values.docOptions,
          filters: resolveFiltersForScope(values.scope),
          includeSourceDocuments: values.includeSourceDocuments,
          orderByDate: values.orderByDate,
        });
      },
      progressEndpoint,
      statusEndpoint,
    };

    const variants: DownloadVariant[] = [tableVariant, writtenVariant];

    const selectedFactsCount = selectedFactIds.size;
    const scopes: { id: string; label: string; totalCount?: number }[] = [];
    if (selectedFactsCount > 0) {
      scopes.push({
        id: selectedScopeId,
        label: `Download ${selectedFactsCount} selected fact${selectedFactsCount !== 1 ? 's' : ''}`,
        totalCount: selectedFactsCount,
      });
    }
    if (filterCount > 0) {
      scopes.push({ id: filteredScopeId, label: 'Download filtered facts', totalCount });
    }
    scopes.push({ id: allScopeId, label: 'Download all facts', totalCount: totalUnfilteredCount });

    const defaultScopeId = selectedFactsCount > 0 ? selectedScopeId : filterCount > 0 ? filteredScopeId : allScopeId;

    return { totalCount: totalUnfilteredCount, scopes, defaultScopeId, variants };
  }, [
    matterId,
    subsetId,
    fetchDownload,
    activeFilters,
    selectedFactIds,
    totalCount,
    totalUnfilteredCount,
    filterCount,
  ]);

  const handleCreateSubsetFromFacts = useCallback(async () => {
    try {
      const result = await resolveFactDocIds.mutateAsync({
        matterId,
        factIds: Array.from(selectedFactIds),
      });
      if (result.docIdCount === 0) {
        toast.warning('No source documents found for the selected facts');
        return;
      }
      navigate(`/matters/${matterId}/subsets/new?factsQueryKey=${result.queryKey}`);
    } catch {
      toast.error('Failed to resolve documents from selected facts');
    }
  }, [matterId, selectedFactIds, resolveFactDocIds, navigate]);

  const handleNewFactCreated = useCallback(() => {
    trackCustomEvent(TrackingEventsV2.FactAddOnFactsPage, { matterId });
    refetchCaseFacts();
    setFactAdderModalIsOpen(false);
  }, [refetchCaseFacts, matterId]);

  const resultsIncludedLength = selectedFactIds.size;

  const { getProcessingState } = useMatterProcessingStore();

  return (
    <>
      <PageHeader>
        <PageTitle text="Facts" showSubsetChip />

        <div className="flex items-center gap-2">
          <PermissionGate permission={Permission.ChronologyCreate} uiMode="disabled">
            <FactChronologyDropdown
              matterId={matterId}
              subsetId={subsetId}
              selectedFactIds={selectedFactIds}
              variant="primary"
            />
          </PermissionGate>

          {/* Permissions handled inside subset dropdown component */}
          <SubsetDropdown
            matterId={matterId}
            subsetId={subsetId}
            disabled={selectedFactIds.size === 0}
            isLoading={isResolvingDocs}
            triggerText="Subset from Sources"
            onCreateNew={handleCreateSubsetFromFacts}
            getDocIds={async () => {
              try {
                const result = await resolveFactDocIds.mutateAsync({
                  matterId,
                  factIds: Array.from(selectedFactIds),
                });
                if (result.docIdCount === 0) {
                  toast.warning('No source documents found for the selected facts');
                  return null;
                }
                return result.docIds;
              } catch {
                toast.error('Failed to resolve documents from selected facts');
                return null;
              }
            }}
          />

          {isLabellingOrg && (
            <>
              <Button disabled={resultsIncludedLength < 2} onClick={() => setClusterModalOpen(true)}>
                <Icon className="lucide--group" />
                Create Cluster
              </Button>

              <Button disabled={resultsIncludedLength < 2} onClick={() => setCreateInconsistencyModalOpen(true)}>
                <Icon className="lucide--triangle-alert" />
                Create Inconsistency
              </Button>
            </>
          )}

          <ButtonGroup>
            <PermissionGate permission={Permission.FactUpdate} uiMode="disabled">
              <UpdateRelevanceButton selectedFactIds={selectedFactIds} onSuccess={() => goToPage(1)} />
            </PermissionGate>
          </ButtonGroup>

          <OverflowMenu ariaLabel="More fact actions">
            <PermissionGate permission={Permission.FactDelete} uiMode="disabled">
              <ArchiveSelectedMenuItem selectedFactIds={selectedFactIds} onSelect={() => setArchiveDialogOpen(true)} />
            </PermissionGate>

            <PermissionGate permission={Permission.FactCreate} uiMode="disabled">
              <DropdownMenuItem
                onClick={() => setFactAdderModalIsOpen(true)}
                disabled={!getProcessingState(matterId)?.hasCompletedRuns}
              >
                <Icon className="lucide--plus" />
                Add Fact
              </DropdownMenuItem>
            </PermissionGate>
            <PermissionGate permission={Permission.FactDownload} uiMode="disabled">
              <DropdownMenuItem onClick={download.open}>
                <Icon className="lucide--download" />
                Download
              </DropdownMenuItem>
            </PermissionGate>
          </OverflowMenu>
          <DownloadFlow controller={download} definition={downloadDefinition} />

          <AddFactDialog
            isOpen={factAdderModalIsOpen}
            onOpenChange={setFactAdderModalIsOpen}
            handleNewFactCreated={handleNewFactCreated}
          />

          <ArchiveSelectedDialog
            open={archiveDialogOpen}
            onOpenChange={setArchiveDialogOpen}
            selectedFactIds={selectedFactIds}
            setSelectedFactIds={setSelectedFactIds}
            onSuccess={() => goToPage(1)}
          />
        </div>
      </PageHeader>

      <div className="flex w-full gap-2">
        <NaturalLanguageSearchBox
          value={globalFilter}
          onChange={setGlobalFilter}
          onSearchCall={onSearchCall}
          clearSearch={clearSearch}
          processingNL={processingNL}
          entityType="facts"
        />

        <ButtonGroup>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={activeFilters.disputedFacts ? 'destructive' : 'default'}
                onClick={() => {
                  setFilters({
                    ...activeFilters,
                    disputedFacts: !activeFilters.disputedFacts,
                  });
                  goToPage(0);
                }}
                className={activeFilters.keyFacts ? 'border-r-0!' : ''}
              >
                <Icon className="lucide--triangle-alert" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Toggle disputed Facts</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={activeFilters.keyFacts ? 'warn' : 'default'}
                onClick={() => {
                  setFilters({
                    ...activeFilters,
                    keyFacts: !activeFilters.keyFacts,
                  });
                  goToPage(0);
                }}
                className={activeFilters.keyFacts ? 'z-10 not-hover:border-l!' : ''}
              >
                <Icon className="lucide--key-round" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Toggle key Facts</TooltipContent>
          </Tooltip>
        </ButtonGroup>

        <FilterFactsDialog
          isOpen={filtersModalOpen}
          onOpenChange={setFiltersModalOpen}
          filterCount={filterCount}
          activeFilters={activeFilters}
          allDocumentTypes={docTypes}
          setFilters={setFilters}
          caseRuns={caseRuns}
          caseIssues={issuesWithCounts}
          goToPage={goToPage}
          totalUnfilteredCount={totalUnfilteredCount}
        />
      </div>

      {clusterModalOpen && (
        <ClusterFactsModal
          isOpen={clusterModalOpen}
          onClose={() => setClusterModalOpen(false)}
          matterId={matterId}
          subsetId={subsetId}
          selectedFactIds={selectedFactIds}
          onSuccess={() => {
            setClusterModalOpen(false);
            onClusterComplete();
          }}
        />
      )}
      {createInconsistencyModalOpen && (
        <CreateInconsistencyModal
          isOpen={createInconsistencyModalOpen}
          onClose={() => setCreateInconsistencyModalOpen(false)}
          selectedFactIds={selectedFactIds}
          onSuccess={() => {
            setCreateInconsistencyModalOpen(false);
            refetchCaseFacts();
          }}
        />
      )}
    </>
  );
};
